import type {
  CatalogDataset,
  ProductFilters,
  ProductView,
} from "./types";
import {
  PERSONAL_CATEGORIES_FILTER,
  PERSONAL_STORES_FILTER,
} from "./personal-filters";
import {
  DEFAULT_APP_LOCALE,
  localeTag,
  type AppLocale,
} from "./locale";
import {
  productSearchKey,
  searchProducts,
} from "./product-search";
import { normalizeSearch } from "./search-normalization";

export { normalizeSearch } from "./search-normalization";

export const MOLDOVA_TIME_ZONE = "Europe/Chisinau";
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1_000;
const moldovaDateFormatter = new Intl.DateTimeFormat("en", {
  timeZone: MOLDOVA_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const priceFormatters: Record<AppLocale, Intl.NumberFormat> = {
  ru: new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  ro: new Intl.NumberFormat("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  uk: new Intl.NumberFormat("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
};

const dateFormatters: Record<AppLocale, Intl.DateTimeFormat> = {
  ru: new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit" }),
  ro: new Intl.DateTimeFormat("ro-RO", { day: "2-digit", month: "2-digit" }),
  uk: new Intl.DateTimeFormat("uk-UA", { day: "2-digit", month: "2-digit" }),
};

export function flattenProducts(dataset: CatalogDataset): ProductView[] {
  const categorySearchText = new Map(
    (dataset.categories ?? []).map((category) => [
      category.id,
      [
        category.label_ru,
        category.label_ro,
        category.label_uk,
      ].join(" "),
    ]),
  );
  return dataset.catalogs.flatMap((catalog) =>
    catalog.products.map((product) => ({
      ...product,
      catalog,
      category_search_text: product.category_id
        ? categorySearchText.get(product.category_id)
        : undefined,
    })),
  );
}

export function dateKeyInMoldova(value: Date): string {
  const parts = Object.fromEntries(
    moldovaDateFormatter
      .formatToParts(value)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function isoDateDayNumber(value: string): number | null {
  if (!ISO_DATE_PATTERN.test(value)) {
    return null;
  }
  const [yearText, monthText, dayText] = value.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return Math.floor(timestamp / DAY_IN_MILLISECONDS);
}

export function formatCompactCatalogDate(value: string): string {
  if (isoDateDayNumber(value) === null) {
    return value;
  }
  return `${value.slice(8, 10)}.${value.slice(5, 7)}`;
}

export function catalogExpiresTodayOrTomorrow(
  validUntil: string,
  today = dateKeyInMoldova(new Date()),
): boolean {
  const validUntilDay = isoDateDayNumber(validUntil);
  const todayDay = isoDateDayNumber(today);
  if (validUntilDay === null || todayDay === null) {
    return false;
  }
  const daysRemaining = validUntilDay - todayDay;
  return daysRemaining === 0 || daysRemaining === 1;
}

export function activeCatalogDataset(
  dataset: CatalogDataset,
  today = dateKeyInMoldova(new Date()),
): CatalogDataset {
  if (!ISO_DATE_PATTERN.test(today)) {
    throw new Error(`Invalid date key: ${today}`);
  }
  const catalogs = dataset.catalogs.filter(
    (catalog) =>
      !ISO_DATE_PATTERN.test(catalog.valid_until) || catalog.valid_until >= today,
  );
  return {
    ...dataset,
    catalog_count: catalogs.length,
    product_count: catalogs.reduce(
      (total, catalog) => total + catalog.products.length,
      0,
    ),
    catalogs,
  };
}

type CategoryTier = 1 | 2 | 3 | 4;
const ALL_CATEGORY_TIERS = [1, 2, 3, 4] as const;
const MIXED_CATEGORY_TIERS = [1, 2, 3] as const;
type MixedCategoryTier = (typeof MIXED_CATEGORY_TIERS)[number];

export const DEFAULT_CATEGORY_TIERS: Readonly<Record<CategoryTier, readonly string[]>> = {
  1: ["dairy_eggs", "meat_deli", "produce", "fish_seafood"],
  2: [
    "bakery",
    "pantry",
    "frozen_ready",
    "soft_drinks",
    "sweets_snacks",
    "household_cleaning",
  ],
  3: ["home_nonfood", "beauty_hygiene"],
  4: [
    "baby",
    "pets",
    "other",
  ],
};

export const DEFAULT_FEED_TIER_WEIGHTS: Readonly<{
  start: Readonly<Record<MixedCategoryTier, number>>;
  end: Readonly<Record<MixedCategoryTier, number>>;
}> = {
  start: { 1: 9, 2: 3, 3: 1 },
  end: { 1: 1, 2: 2, 3: 1 },
};

const CATEGORY_TIER = new Map<string, CategoryTier>(
  (Object.entries(DEFAULT_CATEGORY_TIERS) as Array<
    [string, readonly string[]]
  >).flatMap(([tier, categoryIds]) =>
    categoryIds.map((categoryId) => [categoryId, Number(tier) as CategoryTier]),
  ),
);

interface CategoryQueueState {
  categories: string[];
  queues: Map<string, ProductView[]>;
  cursor: number;
  initial: number;
  remaining: number;
}

function effectiveDiscount(product: ProductView): number {
  const oldPrice = product.old_price_mdl;
  const newPrice = product.new_price_mdl;
  if (
    oldPrice !== null &&
    newPrice !== null &&
    Number.isFinite(oldPrice) &&
    Number.isFinite(newPrice) &&
    oldPrice > 0 &&
    newPrice >= 0 &&
    newPrice < oldPrice
  ) {
    return ((oldPrice - newPrice) / oldPrice) * 100;
  }
  const declared = product.discount_percent;
  return declared !== null &&
    Number.isFinite(declared) &&
    declared >= 0 &&
    declared <= 100
    ? declared
    : -1;
}

function discountOrder(
  originalIndex: ReadonlyMap<ProductView, number>,
): (left: ProductView, right: ProductView) => number {
  return (left, right) =>
    effectiveDiscount(right) - effectiveDiscount(left) ||
    (originalIndex.get(left) ?? 0) - (originalIndex.get(right) ?? 0);
}

function buildCategoryQueue(
  products: readonly ProductView[],
  categoryOrder: readonly string[],
  originalIndex: ReadonlyMap<ProductView, number>,
): CategoryQueueState {
  const queues = new Map<string, ProductView[]>();
  for (const product of products) {
    const categoryId = product.category_id || "other";
    const queue = queues.get(categoryId) ?? [];
    queue.push(product);
    queues.set(categoryId, queue);
  }
  for (const queue of queues.values()) {
    queue.sort(discountOrder(originalIndex));
  }
  const preferred = categoryOrder.filter((categoryId) => queues.has(categoryId));
  const extras = [...queues.keys()]
    .filter((categoryId) => !preferred.includes(categoryId))
    .sort();
  return {
    categories: [...preferred, ...extras],
    queues,
    cursor: 0,
    initial: products.length,
    remaining: products.length,
  };
}

function takeCategoryProduct(
  state: CategoryQueueState,
  recentStores: readonly string[],
): ProductView | undefined {
  if (state.categories.length === 0) {
    return undefined;
  }
  for (let attempt = 0; attempt < state.categories.length; attempt += 1) {
    const index = state.cursor % state.categories.length;
    const categoryId = state.categories[index]!;
    state.cursor = (index + 1) % state.categories.length;
    const queue = state.queues.get(categoryId);
    if (!queue?.length) {
      continue;
    }
    let candidateIndex = 0;
    const repeatedStore =
      recentStores.length >= 2 &&
      recentStores.at(-1) === recentStores.at(-2)
        ? recentStores.at(-1)
        : undefined;
    if (repeatedStore) {
      const alternative = queue
        .slice(0, 5)
        .findIndex((product) => product.catalog.chain !== repeatedStore);
      if (alternative >= 0) {
        candidateIndex = alternative;
      }
    }
    const product = queue.splice(candidateIndex, 1)[0];
    if (product) {
      state.remaining -= 1;
    }
    return product;
  }
  return undefined;
}

function interleaveCategories(
  products: ProductView[],
  categoryOrder: readonly string[],
): ProductView[] {
  const originalIndex = new Map(products.map((product, index) => [product, index]));
  const state = buildCategoryQueue(products, categoryOrder, originalIndex);
  const result: ProductView[] = [];
  const recentStores: string[] = [];
  while (result.length < products.length) {
    const product = takeCategoryProduct(state, recentStores);
    if (!product) {
      break;
    }
    result.push(product);
    recentStores.push(product.catalog.chain);
  }
  return result;
}

function tierWeight(
  tier: MixedCategoryTier,
  state: CategoryQueueState,
): number {
  const start = DEFAULT_FEED_TIER_WEIGHTS.start[tier];
  const end = DEFAULT_FEED_TIER_WEIGHTS.end[tier];
  const progress =
    state.initial <= 0
      ? 1
      : Math.min(
          1,
          Math.max(0, (state.initial - state.remaining) / state.initial),
        );
  return start + (end - start) * progress;
}

function interleaveAdaptiveTiers(
  states: ReadonlyMap<CategoryTier, CategoryQueueState>,
  total: number,
  recentStores: string[],
): ProductView[] {
  const result: ProductView[] = [];
  const credits = new Map<MixedCategoryTier, number>(
    MIXED_CATEGORY_TIERS.map((tier) => [tier, 0]),
  );

  while (result.length < total) {
    const available = MIXED_CATEGORY_TIERS.filter(
      (tier) => (states.get(tier)?.remaining ?? 0) > 0,
    );
    if (available.length === 0) {
      break;
    }
    const weights = new Map<MixedCategoryTier, number>(
      available.map((tier) => [tier, tierWeight(tier, states.get(tier)!)]),
    );
    const weightTotal = available.reduce(
      (sum, tier) => sum + (weights.get(tier) ?? 0),
      0,
    );
    for (const tier of available) {
      credits.set(
        tier,
        (credits.get(tier) ?? 0) + (weights.get(tier) ?? 0) / weightTotal,
      );
    }
    let selectedTier = available[0]!;
    for (const tier of available.slice(1)) {
      if ((credits.get(tier) ?? 0) > (credits.get(selectedTier) ?? 0)) {
        selectedTier = tier;
      }
    }
    const product = takeCategoryProduct(states.get(selectedTier)!, recentStores);
    if (!product) {
      states.get(selectedTier)!.remaining = 0;
      continue;
    }
    credits.set(selectedTier, (credits.get(selectedTier) ?? 0) - 1);
    result.push(product);
    recentStores.push(product.catalog.chain);
  }
  return result;
}

function defaultFeedOrder(products: ProductView[]): ProductView[] {
  const originalIndex = new Map(products.map((product, index) => [product, index]));
  const tierProducts = new Map<CategoryTier, ProductView[]>([
    [1, []],
    [2, []],
    [3, []],
    [4, []],
  ]);
  for (const product of products) {
    const tier = CATEGORY_TIER.get(product.category_id || "other") ?? 4;
    tierProducts.get(tier)?.push(product);
  }
  const states = new Map<CategoryTier, CategoryQueueState>(
    ALL_CATEGORY_TIERS.map((tier) => [
      tier,
      buildCategoryQueue(
        tierProducts.get(tier) ?? [],
        DEFAULT_CATEGORY_TIERS[tier],
        originalIndex,
      ),
    ]),
  );
  const recentStores: string[] = [];
  const mixedTotal = MIXED_CATEGORY_TIERS.reduce(
    (sum, tier) => sum + (states.get(tier)?.remaining ?? 0),
    0,
  );
  const result = interleaveAdaptiveTiers(states, mixedTotal, recentStores);
  const finalTier = states.get(4)!;
  while (finalTier.remaining > 0) {
    const product = takeCategoryProduct(finalTier, recentStores);
    if (!product) {
      break;
    }
    result.push(product);
    recentStores.push(product.catalog.chain);
  }
  return result;
}

export function filterProducts(
  products: ProductView[],
  filters: ProductFilters,
): ProductView[] {
  const normalizedQuery = normalizeSearch(filters.query);
  const searchMatches = normalizedQuery
    ? searchProducts(products, filters.query)
    : null;
  const personalStores = new Set(filters.personalStores ?? []);
  const personalCategoryIds = new Set(filters.personalCategoryIds ?? []);
  const result = products.filter((product) => {
    const personalCategoriesActive =
      filters.categoryId === PERSONAL_CATEGORIES_FILTER;
    const alcoholVisible =
      (Boolean(normalizedQuery) && filters.includeAlcohol === true) ||
      filters.categoryId === "alcohol" ||
      (personalCategoriesActive && personalCategoryIds.has("alcohol"));
    if (product.category_id === "alcohol" && !alcoholVisible) {
      return false;
    }
    if (
      personalCategoriesActive &&
      (!product.category_id || !personalCategoryIds.has(product.category_id))
    ) {
      return false;
    }
    if (
      filters.categoryId !== "all" &&
      !personalCategoriesActive &&
      product.category_id !== filters.categoryId
    ) {
      return false;
    }
    if (
      filters.store === PERSONAL_STORES_FILTER &&
      !personalStores.has(product.catalog.chain)
    ) {
      return false;
    }
    if (
      filters.store !== "all" &&
      filters.store !== PERSONAL_STORES_FILTER &&
      product.catalog.chain !== filters.store
    ) {
      return false;
    }
    if (filters.catalogId !== "all" && product.catalog_id !== filters.catalogId) {
      return false;
    }
    if (!searchMatches) {
      return true;
    }
    return searchMatches.scores.has(productSearchKey(product));
  });

  if (filters.sort === "catalog-order") {
    return result;
  }
  if (filters.sort === "price-asc") {
    result.sort(
      (left, right) =>
        (left.new_price_mdl ?? Number.POSITIVE_INFINITY) -
        (right.new_price_mdl ?? Number.POSITIVE_INFINITY),
    );
  } else if (filters.sort === "discount-desc") {
    result.sort(
      (left, right) =>
        (right.discount_percent ?? -1) - (left.discount_percent ?? -1),
    );
  } else if (searchMatches) {
    const originalIndex = new Map(result.map((product, index) => [product, index]));
    result.sort(
      (left, right) =>
        (searchMatches.scores.get(productSearchKey(right)) ?? 0) -
          (searchMatches.scores.get(productSearchKey(left)) ?? 0) ||
        (originalIndex.get(left) ?? 0) - (originalIndex.get(right) ?? 0),
    );
  } else if (filters.categoryId === PERSONAL_CATEGORIES_FILTER) {
    return interleaveCategories(result, filters.personalCategoryIds ?? []);
  } else if (filters.categoryId !== "all") {
    const originalIndex = new Map(result.map((product, index) => [product, index]));
    result.sort(discountOrder(originalIndex));
  } else {
    return defaultFeedOrder(result);
  }
  return result;
}

export function nextVisibleCount(
  current: number,
  total: number,
  pageSize: number,
): number {
  if (pageSize <= 0) {
    throw new Error("Page size must be positive");
  }
  return Math.min(Math.max(current, 0) + pageSize, Math.max(total, 0));
}

export function formatPrice(
  value: number,
  locale: AppLocale = DEFAULT_APP_LOCALE,
): string {
  return `${priceFormatters[locale].format(value)} lei`;
}

export function formatValidity(
  from: string,
  until: string,
  locale: AppLocale = DEFAULT_APP_LOCALE,
): string {
  const fromDate = new Date(`${from}T00:00:00`);
  const untilDate = new Date(`${until}T00:00:00`);
  if (Number.isNaN(fromDate.valueOf()) || Number.isNaN(untilDate.valueOf())) {
    return `${from} — ${until}`;
  }
  const formatter = dateFormatters[locale];
  return `${formatter.format(fromDate)} — ${formatter.format(untilDate)}`;
}

export function uniqueStores(
  dataset: CatalogDataset,
  locale: AppLocale = DEFAULT_APP_LOCALE,
): string[] {
  return [...new Set(dataset.catalogs.map((catalog) => catalog.chain))].sort(
    (left, right) => left.localeCompare(right, localeTag(locale)),
  );
}
